import IProduct, { BatteryTypeEnum } from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import {
  isNewFood,
  isNewNonFood,
  isFeedCategory,
  isFish,
  isEgg,
  isBeverages,
  isFruktOrGreen,
  isFoodSupplements,
  isSpecialFoodNutrition,
  isToy,
  isElectric,
  isJewelry,
  isGoods,
  isMedical,
  isChemical,
  isDetergent,
  isCosmetic,
  isSunscreen,
  isComplementarySupplements,
  isOTC,
  isOtherFoodstuffs,
  isMeat,
  isOtherFoodGroup,
  isSpicesSoupsSalads,
  isWhiteSnus,
  isBurningCandles,
  isSewingKnittingYarn,
  isElectricBattery,
  isElectricLamp,
  isPharma,
  isCmrHazardCategory,
} from '../fields/category';
import { hasPhraseFillControls, shouldShowDailyDose, shouldShowHazardDetails } from './aiReviewFields';

// Display rules only; requiredness and validation keep their existing owners.
export const canAddExtraQuantity = (product: Partial<IProduct>, isFood?: boolean) =>
  !!isFood && product.descriptive_size_unit === 'GRAM_APPROXIMATE';

interface ProductFieldVisibilityContext {
  product: Partial<IProduct>;
  rootCategory?: IFoodlaCategoryOption;
  showApoteaFields?: boolean;
}

export function getProductFieldVisibility({
  product,
  rootCategory,
  showApoteaFields = false,
}: ProductFieldVisibilityContext) {
  const category = product.foodlaCategory;
  const food = isNewFood(category, rootCategory);
  const nonFood = isNewNonFood(category, rootCategory);
  const feed = isFeedCategory(category, rootCategory);
  const fish = !!isFish(category, rootCategory, product as IProduct);
  const egg = isEgg(category, rootCategory);
  const fruit = isFruktOrGreen(category, rootCategory);
  const beverages = isBeverages(category, rootCategory);
  const supplements = isFoodSupplements(category, rootCategory);
  const specialNutrition = isSpecialFoodNutrition(category, rootCategory);
  const toy = isToy(category, rootCategory);
  const electrical = isElectric(category, rootCategory);
  const jewelry = isJewelry(category, rootCategory);
  const goods = isGoods(category, rootCategory);
  const medical = isMedical(category, rootCategory);
  const chemical = isChemical(category, rootCategory);
  const detergent = isDetergent(category, rootCategory);
  const cosmeticOrSunscreen = isCosmetic(category, rootCategory) || isSunscreen(category, rootCategory);
  const otc = isOTC(category, rootCategory);
  const complementaryFeed = isComplementarySupplements(category, rootCategory);
  const notStore = !product.producerIsStore;
  const ordinaryGoods = goods || jewelry || toy || electrical;
  const goodsOrMedical = ordinaryGoods || medical;
  const chemicalRules = toy || chemical || detergent;
  const otherFood =
    isOtherFoodstuffs(category, rootCategory) ||
    isMeat(category, rootCategory) ||
    isOtherFoodGroup(category, rootCategory) ||
    isSpicesSoupsSalads(category, rootCategory);
  const preparation = fish || specialNutrition || beverages || egg || fruit || otherFood;
  const manufacturing = nonFood || fish || specialNutrition || beverages || supplements || fruit || otherFood;
  const hazardDetails = nonFood && !feed && shouldShowHazardDetails(product, rootCategory);
  const cmr = !food && notStore && (isPharma(category, rootCategory) || isCmrHazardCategory(category, rootCategory));
  const reach = !food && notStore && (goodsOrMedical || chemical || detergent || cosmeticOrSunscreen);
  const pops = goodsOrMedical || chemical || detergent;
  const storePack = !!(product.storePackOrderableUnit || product.storePackDespatchUnit);
  const transport = !!(product.transportOrderableUnit || product.transportDespatchUnit);
  const pallet = !!(product.palletOrderableUnit || product.palletDespatchUnit);
  // Apotea needs all four kfp ratios. Other retailers hide ratios derivable
  // from the packaging chain when all three packaging levels are selected.
  const allPackLevels = !showApoteaFields && storePack && transport && pallet;

  const sectionVisible = {
    category: true,
    identity: true,
    contents: food || feed,
    fish,
    feed,
    ingredients: true,
    nutrition: food,
    vitamins: food,
    substances: specialNutrition || supplements,
    properties: !isWhiteSnus(category, rootCategory) && goodsOrMedical,
    candles: isBurningCandles(category, rootCategory),
    yarn: isSewingKnittingYarn(category, rootCategory),
    textile: isSewingKnittingYarn(category, rootCategory),
    snus: isWhiteSnus(category, rootCategory),
    batteries: isElectricBattery(category, rootCategory),
    lamps: isElectricLamp(category, rootCategory),
    quantity: true,
    grossWeight: !(product.producerIsStore && product.producerOrg?.startsWith('coop/')),
    dimensions: !(product.producerIsStore && product.producerOrg?.startsWith('coop/')),
    storageTemperature: true,
    transportTemperature: notStore,
    storageInstructions: true,
    preparation,
    preparedForm: isPharma(category, rootCategory) || detergent || complementaryFeed || specialNutrition || supplements,
    dosage: (nonFood || specialNutrition || supplements) && !feed,
    hazards: nonFood && !feed,
    waste: true,
    origin: food || feed,
    manufacturing,
    characteristics: nonFood,
    brand: true,
    distributor: true,
    contact: !!product.producerIsMultiBrand,
    search: true,
    prices: showApoteaFields,
    orderable: notStore,
    despatch: notStore,
    packQuantities: notStore,
    dates: notStore,
    commodity: showApoteaFields,
    location: notStore,
    tax: true,
    weightControl: specialNutrition,
    feedCompliance: feed,
    productCompliance: ordinaryGoods || medical || chemical || detergent,
    cosmeticCompliance: cosmeticOrSunscreen,
    reach: chemicalRules,
    hazardousSubstances: ordinaryGoods,
    medical,
    ce: goodsOrMedical,
    packaging: notStore,
    quality: true,
  };

  // Conditions here only narrow fields inside an applicable section.
  const fieldVisible: Partial<Record<keyof IProduct, boolean>> = {
    eggWeightClass: egg,
    classification: fruit,
    alcoholPercentage: beverages,
    recyclingType: beverages,
    recycling_amount: beverages,
    purchasingDataSupplierArticleNo: notStore,
    eggPackerApprovalNumber: egg,
    shortSalesDescription: showApoteaFields,
    animalFeedType: feed,
    ingredient_statement: food,
    non_food_ingredients: goodsOrMedical || chemical || detergent || cosmeticOrSunscreen || complementaryFeed || otc,
    dagsdosAmount: shouldShowDailyDose(product, specialNutrition || supplements),
    dagsdosUnit: shouldShowDailyDose(product, specialNutrition || supplements),
    color: ordinaryGoods,
    batteryTypeOther: product.batteryType === BatteryTypeEnum.OTHER,
    descriptive_size_amount_extra:
      Array.isArray(product.descriptive_size_amount_extra) && product.descriptive_size_amount_extra.length > 0,
    hazardSymbols: hazardDetails,
    // Products carry CLP codes; HazardSymbolsEnum contains translated display labels.
    flammableSubtype:
      hazardDetails && (product.hazardSymbols as unknown as string[] | undefined)?.includes('FLAMMABLE') === true,
    hazardStatements: hazardDetails,
    hazardStatementFills: hazardDetails && hasPhraseFillControls(product.hazardStatements),
    securityNotices: hazardDetails,
    securityNoticeFills: hazardDetails && hasPhraseFillControls(product.securityNotices),
    brand_food: food,
    brand: !food,
    consumer_size_to_order_size_ratio: showApoteaFields || storePack,
    palletQtyOfStorePack: !showApoteaFields && storePack && pallet,
    transportQtyOfNextLowerItem: !showApoteaFields && storePack && transport,
    palletQtyOfNextLowerItem: !showApoteaFields && !allPackLevels && transport && pallet,
    transportQtyOfBaseItem: showApoteaFields || (!allPackLevels && transport),
    lavQuantity: showApoteaFields || (!allPackLevels && pallet),
    palletQtyOfBaseItem: showApoteaFields || (!allPackLevels && pallet),
    complianceMetalsSkinContact: jewelry,
    confirmGuidanceSwedishElectronics: toy || electrical || medical,
    complianceToyDirective: toy || electrical,
    complianceBiocid: chemicalRules,
    complianceBiocidalTreated: ordinaryGoods,
    regulatedSubstance: !!product.reachSubstancesAnnex19072006,
    informationHazardousSubstances: !!product.hazardousSubstancesOver1,
    specifyCEStandards: !!product.ceMarking,
    childrenUnder16: food || ordinaryGoods || chemical || detergent || cosmeticOrSunscreen || otc,
    compliancePpwrPackaging: showApoteaFields,
    containsBattery: goodsOrMedical,
    compliancePops: pops,
    containsBannedSubstancesPops: pops,
    cmrSubstance: cmr,
    CMRSubstances: cmr && !!product.cmrSubstance,
    reachSubstance: reach,
    qrRelevantFeedCertifications: showApoteaFields && feed,
    qrFoodHygiene: showApoteaFields && notStore && food,
    qrElectronicCode: showApoteaFields && (toy || electrical || medical),
    qrClassificationLabelingPackaging: showApoteaFields && chemicalRules,
    qrLabelingSwedish: showApoteaFields && cosmeticOrSunscreen,
    qrMedicalClaims: showApoteaFields && cosmeticOrSunscreen,
    qrMarketingClaimsCosmetic: showApoteaFields && cosmeticOrSunscreen,
    qrAdditivesInFeed: showApoteaFields && feed,
    qrFeedLabeling: showApoteaFields && feed,
    qrMisleadingSalesTerms: showApoteaFields && notStore && (food || feed),
    qrNutritionClaimsCode: showApoteaFields && notStore && food,
    qrHealthClaimsCode: showApoteaFields && notStore && food,
    qrFoodLabeling: showApoteaFields && notStore && food,
    qrEfsaUlValues: showApoteaFields && notStore && supplements,
  };

  return { sections: sectionVisible, fields: fieldVisible };
}

export type ProductFieldVisibility = ReturnType<typeof getProductFieldVisibility>;
export type ProductFieldGroup = keyof ProductFieldVisibility['sections'];
